import React, { useState, useEffect, useContext } from "react";
import styles from "./ProductJourney.module.css";
import DropArrow from "../../assets/images/CaretDown.png";
import { ProductContext } from "../../context/ProductContext";

type JourneyStep = {
  title: string;
  time: string;
  description: string;
};

function decryptBase64(base64Str: string): string {
  const neededPadding = base64Str.length % 4;
  if (neededPadding) {
    base64Str += "====".slice(0, 4 - neededPadding);
  }
  return window.atob(base64Str);
}

export const ProductJourney: React.FC = () => {
  const { product } = useContext(ProductContext)!;
  const [journeySteps, setJourneySteps] = useState<JourneyStep[]>([]);
  const [expanded, setExpanded] = useState<boolean[]>([]);
  const [isVisible, setIsVisible] = useState<boolean>(false);

  const headingColor = product?.headingColor || "#000";
  const textColor    = product?.textColor    || "#000";
  const fontStyle    = product?.fontStyle    || "inherit";

  // Helper: build 20 dummy dates in April 2025
  const dummyDates = React.useMemo(() => {
    return Array.from({ length: 20 }, (_, i) => {
      const d = new Date(2025, 3, i + 1); // April (0-based index)
      return d.toLocaleDateString("en-US", {
        month: "short",
        day:   "numeric",
        year:  "numeric",
      });
    });
  }, []);

  useEffect(() => {
    if (!product) return;

    const loadJourney = async () => {
      const params   = new URLSearchParams(window.location.search);
      const uidParam = params.get("uid") || "";

      // —— decrypt if needed ——
      let finalUid: string;
      if (uidParam.startsWith("17")) {
        finalUid = uidParam;
      } else {
        finalUid = decryptBase64(uidParam);
      }

      // —— now extract SKU before the first "x" ——
      const skuid = finalUid.split("x")[0];
      const url   = `https://ezeclit8vi.execute-api.ap-south-1.amazonaws.com/prod/product-journey?skuid=${skuid}`;

      try {
        const res = await fetch(url);
        if (!res.ok) {
          setIsVisible(false);
          return;
        }
        const data = await res.json();
        const steps: JourneyStep[] = data.ProductJourney.map((item: any, idx: number) => ({
          title:       item.title,
          description: item.description,
          time:        dummyDates[idx] || dummyDates[0],
        }));

        setJourneySteps(steps);
        setIsVisible(true);
      } catch (err) {
        console.error("Error fetching product journey:", err);
        setIsVisible(false);
      }
    };

    loadJourney();
  }, [product, dummyDates]);

  // Initialize expand/collapse state on data change
  useEffect(() => {
    setExpanded(new Array(journeySteps.length).fill(false));
  }, [journeySteps]);

  const toggle = (i: number) =>
    setExpanded((prev) => prev.map((v, idx) => (idx === i ? !v : v)));

  // If API didn't return 200, or data not yet available, render nothing
  if (!isVisible) return null;

  return (
    <div className="w-full max-w-2xl mx-auto mt-5 font-mulish mb-2">
      <h2
        className="px-4 pb-4 text-base font-bold"
        style={{
          color:         headingColor,
          fontFamily:    fontStyle,
          letterSpacing: "0.01em",
        }}
      >
        Product Journey
      </h2>

      <div className={styles.timelineContainer}>
        <ul className={styles.timelineList}>
          {journeySteps.map((step, i) => (
            <li
              key={i}
              className={`${styles.timelineItem} ${expanded[i] ? styles.expanded : ""}`}
            >
              <div
                className={styles.timelineMarker}
                style={{ background: headingColor, borderColor: headingColor }}
              />

              <div className={styles.timelineContent}>
                <div
                  className="font-bold"
                  style={{
                    color:      headingColor,
                    fontFamily: fontStyle,
                    fontSize:   15,
                  }}
                >
                  {step.title}
                </div>
                <div
                  style={{
                    color:      textColor,
                    fontFamily: fontStyle,
                    fontSize:   12,
                  }}
                >
                  {step.time}
                </div>
                <div
                  className={`${styles.description} ${expanded[i] ? styles.showDescription : ""}`}
                  style={{
                    color:      textColor,
                    fontFamily: fontStyle,
                    fontSize:   12,
                  }}
                >
                  {step.description}
                </div>
              </div>

              <img
                src={DropArrow}
                alt="toggle"
                className={`${styles.dropdownIcon} ${expanded[i] ? styles.rotate : ""}`}
                onClick={() => toggle(i)}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ProductJourney;
